import NavItem from './NavItem';
import MenuLinks from './config';
import PropTypes from 'prop-types';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import React, { useEffect, useState } from 'react';
import MyAvatar from 'src/components/MyAvatar';
import Scrollbars from 'src/components/Scrollbars';
import { Link as RouterLink, useLocation, matchPath } from 'react-router-dom';
import { alpha, makeStyles,useTheme } from '@material-ui/core/styles';
import {
  Box,
  Link,
  List,
  Button,
  Drawer,
  Hidden,
  Typography,
  ListSubheader
} from '@material-ui/core';
import firebase from 'firebase/app';
import _ from 'lodash';

import { getCountBadgeActionForRetailer } from 'src/api/badgeAction';
import { PATH_CUSTOMER_MOBILE } from 'src/routes/paths';
import { useSelector } from 'react-redux';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.mode === 'light';

  return {
    drawer: {
      [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
        width: DRAWER_WIDTH
      }
    },
    drawerPaper: {
      width: DRAWER_WIDTH
    },
    subHeader: {
      ...theme.typography.overline,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(5),
      color: theme.palette.text.primary
    },
    account: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 2.5),
      margin: theme.spacing(1, 2.5, 5),
      borderRadius: theme.shape.borderRadiusSm,
      background: theme.palette.grey[isLight ? 200 : 800]
    }
  };
});

// ----------------------------------------------------------------------


function reduceChild({ array, item, pathname, level, listBadgeAction }) {
  const key = item.href + level;

  const badgeContentFormat = !_.isEmpty(item?.badgeModule) ? listBadgeAction[item?.badgeModule] : '';

  if (item.items) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        badgeModule={item.badgeModule}
        badgeContent={badgeContentFormat}
        open={Boolean(match)}
        disabledCollapse={item?.disabledCollapse}
      >
        {renderNavItems({
          pathname,
          level: level + 1,
          items: item.items
        })}
      </NavItem>
    ];
  } else {
    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        href={item.href}
        icon={item.icon}
        info={item.info}
        title={item.title}
        badgeModule={item.badgeModule}
        badgeContent={badgeContentFormat}
      />
    ];
  }
  return array;
}

function renderNavItems({ items, pathname, level = 0, listBadgeAction }) {
  return (
    <List disablePadding>
      {items.reduce(
        (array, item) => reduceChild({ array, item, pathname, level, listBadgeAction }),
        []
      )}
    </List>
  );
}

NavBar.propTypes = {
  onCloseNav: PropTypes.func,
  isOpenNav: PropTypes.bool
};

function NavBar({ isOpenNav, onCloseNav}) {
  const classes = useStyles();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { distributors } = useSelector((state) => state.authFirebase);

  const [modifiedMenuLinks, setModifiedMenuLinks] = useState([])
  const [open, setOpen] = React.useState(false);

  const firebaseUser = firebase.auth().currentUser
  
  const [listBadgeAction, setListBadgeAction] = useState({});



  const routeLogo = () => {
    if (pathname === '/customer-mobile/dashboard') {
      window.location.reload();
    }
  };

  useEffect(async () => {
    if (isOpenNav && onCloseNav) {
      onCloseNav();
    }
    
    let belongToDistributor = user.retailerID?.belongToDistributor
    let retailerAccessSetting = user.retailerID?.distributorAccessSetting

    // Filter retailerAccessSetting based on belongToDistributor
    const filteredAccessSetting = retailerAccessSetting.filter((elem) =>
      belongToDistributor.includes(elem.distributorID.toString())
    );

    // Precompute for faster lookup
    //const hasDefaultAccess = filteredAccessSetting.some(e => e.accessType === "default");
    const customAccessItems = filteredAccessSetting.flatMap(e => e.accessItem || []);

    const modifiedMenuLinksData = MenuLinks.map((obj) => ({
      ...obj,
      items: obj.items.filter((item) => {
        // Hide for staff rule
        if (user.isStaff && item.hideForStaff) return false;

        const a = item.access;
        const staffAccessKey = item.staffAccess;

        if (user.isStaff) {
          // Staff access check
          const hasStaffPermission = !staffAccessKey || (
            Array.isArray(user.staffAccess) &&
            user.staffAccess.includes(staffAccessKey)
          );

          // Staff must be allowed by distributor (default or custom)
          const allowedByDistributor =
            !a || customAccessItems.includes(a);

          return hasStaffPermission && allowedByDistributor;
        } else {
          //const isDefaultAccess = filteredAccessSetting.some((elem) => elem.accessType === "default");
          const isCustomAccess = !item.access || (filteredAccessSetting.some((elem) => elem.accessItem.includes(item.access)) && item.access !== "allOrder");
          
          const allOrderAccess = item.access === "allOrder" && filteredAccessSetting.some((elem) => ((elem.accessType === "custom" && (elem.accessItem.includes("orders") && elem.accessItem.includes("consignment")))));
          const staffPreOrderAccess = item.access === "staffPreOrder" && user.numberStaff > 0;
          return isCustomAccess || allOrderAccess || staffPreOrderAccess;
        }
      }),
    }));

    let finModified = []
    for (const item of modifiedMenuLinksData) {
      if (item.items.length > 0) {
        finModified.push(item)
      }
    }

    // ADD DISTRIBUTORS TO CATALOGUE PATH
    const instockPath = finModified.find((item) => item.subheader === 'instock');
    if (instockPath){
      const cataloguePath = instockPath.items.find((item) => item.title === 'catalogue');
      if (cataloguePath && cataloguePath.items.length === 0) {
        for (const distributor of distributors) {
          const distributorAccessSetting = filteredAccessSetting.find((elem) => elem.distributorID === distributor._id);
          if (distributorAccessSetting){
            if (
              distributorAccessSetting.accessType === 'default' ||
              (distributorAccessSetting.accessType === 'custom' &&
                distributorAccessSetting.accessItem.includes('catalogue')) ||
              (distributorAccessSetting.accessType === 'defaultPreOrder' &&
                distributorAccessSetting.accessItem.includes('catalogue'))
            ) {
              cataloguePath.items.push({
                title: distributor.companyName,
                href: `${PATH_CUSTOMER_MOBILE.general.catalogue}/${distributor._id}`,
              });
            }
          }
        }
      }
    }
    
    setModifiedMenuLinks(finModified)

    const token = await firebaseUser.getIdToken();

    const result = await getCountBadgeActionForRetailer(token, {
      userID: user._id
    })

    setListBadgeAction(result)
  }, [pathname, distributors])



  const renderContent = (
    <Scrollbars >    
      <Box sx={{ px: 2.5, py: 3 }}>
        <RouterLink to="/customer-mobile/dashboard" onClick={routeLogo}>
          <Logo />
        </RouterLink>
      </Box>

      <Link
        underline="none"
        component={RouterLink}
        to="/customer-mobile/dashboard"
      >
        <div className={classes.account}>
          <MyAvatar/>
          <Box sx={{ ml: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              {user.firstName}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {user.isStaff ? 'Staff' : 'Owner'}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', maxWidth: '140px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {user?.retailerID?.companyName}
            </Typography>
          </Box>
        </div>
      </Link>

      {modifiedMenuLinks.map((list) => (
        <List
          disablePadding
          key={list.subheader}
          subheader={
            <ListSubheader
              disableSticky
              disableGutters
              className={classes.subHeader}
            >
              {list.subheader}
            </ListSubheader>
          }
        >
          {renderNavItems({
            items: list.items,
            pathname: pathname,
            listBadgeAction: listBadgeAction
          })}
        </List>
      ))}
    </Scrollbars>
  );

  return (
    <nav className={classes.drawer}>
      <Drawer
        anchor="left"
        open={isOpenNav}
        variant="temporary"
        onClose={onCloseNav}
        classes={{ paper: classes.drawerPaper }}
      >
        {renderContent}
      </Drawer>
    </nav>
  );
}

export default NavBar;